import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepperInputProps {
  label: string;
  value: number;
  onChange: (val: number) => void;
  step?: number;
  min?: number;
  max?: number;
  unit?: string;
  className?: string;
}

export function StepperInput({
  label,
  value,
  onChange,
  step = 10,
  min = 0,
  max = 99999,
  unit,
  className
}: StepperInputProps) {
  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val)) {
      onChange(Math.min(max, Math.max(min, val)));
    }
  };

  return (
    <div className={cn("flex flex-col gap-1.5 w-full", className)}>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center h-12 rounded-lg border border-slate-300 bg-white shadow-sm overflow-hidden focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 transition-all">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={value <= min}
          className="flex-none w-14 h-full flex items-center justify-center bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed border-r border-slate-200 transition-colors"
          aria-label={`Decrease ${label}`}
        >
          <Minus size={20} />
        </button>
        <div className="flex-1 relative flex items-center justify-center h-full">
          <input
            type="number"
            value={value || ''}
            onChange={handleChange}
            className="w-full h-full text-center text-slate-900 font-medium outline-none appearance-none bg-transparent pr-10"
          />
          {unit && (
            <span className="absolute right-3 text-slate-400 text-sm font-medium pointer-events-none">
              {unit}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleIncrement}
          disabled={value >= max}
          className="flex-none w-14 h-full flex items-center justify-center bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed border-l border-slate-200 transition-colors"
          aria-label={`Increase ${label}`}
        >
          <Plus size={20} />
        </button>
      </div>
    </div>
  );
}
